/**
 * Tickets Store - Zustand state management for tickets
 */

import { create } from 'zustand';
import type { TicketWithProperty, TicketPurchaseResult } from '@raffle/types';
import { api } from '../lib/api';

interface TicketsState {
  tickets: TicketWithProperty[];
  isLoading: boolean;
  error: string | null;
  totalPages: number;
  currentPage: number;
  isPurchasing: boolean;
  
  // Actions
  fetchTickets: (params?: { page?: number; propertyId?: string; status?: string }) => Promise<void>;
  purchaseTickets: (propertyId: string, quantity: number, useCredits?: boolean) => Promise<TicketPurchaseResult | null>;
  clearError: () => void;
}

export const useTicketsStore = create<TicketsState>((set) => ({
  tickets: [],
  isLoading: false,
  error: null,
  totalPages: 1,
  currentPage: 1,
  isPurchasing: false,

  fetchTickets: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const result = await api.tickets.list({
        page: params.page || 1,
        pageSize: 20,
        propertyId: params.propertyId,
        status: params.status,
      });

      if (result.success && result.data) {
        set({
          tickets: result.data.items,
          totalPages: result.data.meta.totalPages,
          currentPage: result.data.meta.page,
          isLoading: false,
        });
      } else {
        set({ error: result.error?.message || 'Failed to fetch tickets', isLoading: false });
      }
    } catch (error) {
      set({ error: 'Failed to fetch tickets', isLoading: false });
    }
  },

  purchaseTickets: async (propertyId: string, quantity: number, useCredits = false) => {
    set({ isPurchasing: true, error: null });
    try {
      const result = await api.tickets.purchase({
        propertyId,
        quantity,
        useCredits,
      });

      if (result.success && result.data) {
        set({ isPurchasing: false });
        return result.data;
      } else {
        set({ error: result.error?.message || 'Failed to purchase tickets', isPurchasing: false });
        return null;
      }
    } catch (error) {
      set({ error: 'Failed to purchase tickets', isPurchasing: false });
      return null;
    }
  },

  clearError: () => set({ error: null }),
}));
